import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import api from './api';

export const SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'NZD', 'ZAR', 'SGD', 'HKD', 'PHP',
] as const;

export const CURRENCY_META: Record<string, { symbol: string; flag: string }> = {
  USD: { symbol: '$', flag: '🇺🇸' },
  EUR: { symbol: '€', flag: '🇪🇺' },
  GBP: { symbol: '£', flag: '🇬🇧' },
  JPY: { symbol: '¥', flag: '🇯🇵' },
  AUD: { symbol: 'A$', flag: '🇦🇺' },
  CAD: { symbol: 'C$', flag: '🇨🇦' },
  CHF: { symbol: 'Fr', flag: '🇨🇭' },
  NZD: { symbol: 'NZ$', flag: '🇳🇿' },
  ZAR: { symbol: 'R', flag: '🇿🇦' },
  SGD: { symbol: 'S$', flag: '🇸🇬' },
  HKD: { symbol: 'HK$', flag: '🇭🇰' },
  PHP: { symbol: '₱', flag: '🇵🇭' },
};

interface CurrencyContextValue {
  homeCurrency: string;
  rates: Record<string, number>;
  convert: (amount: number, fromCurrency: string) => number;
  setHomeCurrency: (code: string) => void;
  isLoading: boolean;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

const STORAGE_KEY = 'forgealpha-home-currency';

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [homeCurrency, setHomeCurrencyState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || 'USD',
  );
  const [rates, setRates] = useState<Record<string, number>>({ USD: 1 });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user profile for home_currency and exchange rates on mount
  useEffect(() => {
    const token = localStorage.getItem('forgealpha_token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    Promise.allSettled([
      api.get<{ home_currency: string }>('/auth/me'),
      api.get<{ rates: Record<string, number> }>('/auth/exchange-rates'),
    ]).then(([meResult, ratesResult]) => {
      if (meResult.status === 'fulfilled') {
        const serverCurrency = meResult.value.data.home_currency || 'USD';
        const localCurrency = localStorage.getItem(STORAGE_KEY);
        if (localCurrency && localCurrency !== serverCurrency) {
          // Local selection is newer — push it to the server
          api.patch('/auth/preferences', { home_currency: localCurrency }).catch(() => {});
        } else {
          // Server is authoritative — sync to local
          setHomeCurrencyState(serverCurrency);
          localStorage.setItem(STORAGE_KEY, serverCurrency);
        }
      }
      if (ratesResult.status === 'fulfilled' && ratesResult.value.data.rates) {
        setRates(ratesResult.value.data.rates);
      }
      setIsLoading(false);
    });
  }, []);

  // Refresh rates every hour
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get<{ rates: Record<string, number> }>('/auth/exchange-rates');
        if (res.data.rates) setRates(res.data.rates);
      } catch {}
    }, 3600_000);
    return () => clearInterval(interval);
  }, []);

  const setHomeCurrency = useCallback(async (code: string) => {
    setHomeCurrencyState(code);
    localStorage.setItem(STORAGE_KEY, code);
    try {
      await api.patch('/auth/preferences', { home_currency: code });
    } catch {}
  }, []);

  const convert = useCallback(
    (amount: number, fromCurrency: string): number => {
      if (fromCurrency === homeCurrency) return amount;
      const fromRate = rates[fromCurrency];
      const toRate = rates[homeCurrency];
      if (!fromRate || !toRate) return amount;
      return (amount / fromRate) * toRate;
    },
    [rates, homeCurrency],
  );

  return (
    <CurrencyContext.Provider value={{ homeCurrency, rates, convert, setHomeCurrency, isLoading }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
